import { format, subMonths } from 'date-fns'
import deepEqual from 'fast-deep-equal'
import * as qs from 'qs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  ReportListFilters,
  isValidFilterValueOf,
} from 'src/features/reports/listBetaFilters/types'
import { isValidDateString } from 'src/util/datetime/isValidDateString'
import { removeMatchingKeysAndValues } from 'src/util/removeMatchingKeysAndValues'

import { TraceKeyResponseTypeEnum } from '@ulysses-inc/harami_api_client'
import Yup from 'src/features/validation/yup'

const traceKeyConditionSchema = Yup.object({
  traceKeyId: Yup.number().required(),
  responseType: Yup.string()
    .oneOf([
      TraceKeyResponseTypeEnum.ResponseSet,
      TraceKeyResponseTypeEnum.Date,
      TraceKeyResponseTypeEnum.Text,
    ])
    .required(),
  responseSet: Yup.mixed().when('responseType', {
    is: TraceKeyResponseTypeEnum.ResponseSet,
    then: Yup.object({
      value: Yup.string().min(1, '1文字以上入力してください').required(),
    }).required(),
    otherwise: Yup.mixed().notRequired(),
  }),
  datetime: Yup.mixed().when('responseType', {
    is: TraceKeyResponseTypeEnum.Date,
    then: Yup.object({
      greaterThanOrEqual: Yup.date().required('開始日を入力してください'),
      lessThan: Yup.date().required('終了日を入力してください'),
    }).required(),
    otherwise: Yup.mixed().notRequired(),
  }),
  text: Yup.mixed().when('responseType', {
    is: TraceKeyResponseTypeEnum.Text,
    then: Yup.object({
      value: Yup.string().min(1, '1文字以上入力してください').required(),
    }).required(),
    otherwise: Yup.mixed().notRequired(),
  }),
})
export const traceKeyConditionArraySchema = Yup.array().of(
  traceKeyConditionSchema,
)

/**
 * デフォルトの日付範囲（直近1ヶ月）を取得する
 */
export const getDefaultDateRange = (): { dateFrom: string; dateTo: string } => {
  const today = new Date()
  const oneMonthAgo = subMonths(today, 1)
  return {
    dateFrom: format(oneMonthAgo, 'yyyy-MM-dd'),
    dateTo: format(today, 'yyyy-MM-dd'),
  }
}

/**
 * 初期フィルター値を取得する
 * 日付は動的に直近1ヶ月を設定
 */
export const getInitialFilters = (): ReportListFilters => {
  const { dateFrom, dateTo } = getDefaultDateRange()
  return {
    dateFrom,
    dateTo,
    deviatedAnswer: [],
    employeeNameOrCode: '',
    page: 1,
    pageSize: 50,
    placeNodeUuids: [],
    reportName: '',
    statuses: [],
    traceKeyConditions: [],
  }
}

// 後方互換性のため、initialFiltersも残す（ただし日付はnullのまま）
// URL比較用に使用される
export const initialFilters: ReportListFilters = {
  dateFrom: null,
  dateTo: null,
  deviatedAnswer: [],
  employeeNameOrCode: '',
  page: 1,
  pageSize: 50,
  placeNodeUuids: [],
  reportName: '',
  statuses: [],
  traceKeyConditions: [],
}

const qsOptions = {
  parse: { ignoreQueryPrefix: true },
  stringify: {
    skipNulls: true,
    encode: false,
  },
}

/**
 * 型の保証と不正な値のデフォルト値へのフォールバックを行う
 */
const sanitize = {
  dateFrom: (arg: unknown): string => {
    if (isValidDateString(arg)) {
      return arg
    }
    return getDefaultDateRange().dateFrom
  },
  dateTo: (arg: unknown): string => {
    if (isValidDateString(arg)) {
      return arg
    }
    return getDefaultDateRange().dateTo
  },
  deviatedAnswer: (arg: unknown): ReportListFilters['deviatedAnswer'] => {
    if (Array.isArray(arg)) {
      return arg.filter(isValidFilterValueOf.deviatedAnswer)
    }
    return initialFilters.deviatedAnswer
  },
  employeeNameOrCode: (arg: unknown): string => {
    if (typeof arg === 'string') {
      return arg
    }
    return initialFilters.employeeNameOrCode
  },
  page: (arg: unknown): number => {
    const parsedArg = Number(arg)
    if (parsedArg >= 1) return parsedArg
    return initialFilters.page
  },
  pageSize: (arg: unknown): number => {
    const parsedArg = Number(arg)
    if (parsedArg >= 1 && parsedArg <= 100) return parsedArg
    return initialFilters.pageSize
  },
  placeNodeUuids: (arg: unknown): string[] => {
    if (Array.isArray(arg)) {
      return arg.filter((uuid): uuid is string => typeof uuid === 'string')
    }
    return initialFilters.placeNodeUuids
  },
  reportName: (arg: unknown): string => {
    if (typeof arg === 'string') {
      return arg
    }
    return initialFilters.reportName
  },
  statuses: (arg: unknown): ReportListFilters['statuses'] => {
    if (Array.isArray(arg)) {
      return arg.filter(isValidFilterValueOf.status)
    }
    return initialFilters.statuses
  },
  traceKeyConditions: (
    arg: unknown,
  ): ReportListFilters['traceKeyConditions'] => {
    try {
      const validated = traceKeyConditionArraySchema.validateSync(arg)
      if (!validated) {
        return initialFilters.traceKeyConditions
      }
      return validated.map(v => {
        switch (v.responseType) {
          case TraceKeyResponseTypeEnum.ResponseSet:
            return {
              traceKeyId: v.traceKeyId,
              responseType: TraceKeyResponseTypeEnum.ResponseSet,
              responseSet: v.responseSet,
            }
          case TraceKeyResponseTypeEnum.Date:
            return {
              traceKeyId: v.traceKeyId,
              responseType: TraceKeyResponseTypeEnum.Date,
              datetime: v.datetime,
            }
          case TraceKeyResponseTypeEnum.Text:
            return {
              traceKeyId: v.traceKeyId,
              responseType: TraceKeyResponseTypeEnum.Text,
              text: v.text,
            }
          default:
            throw new Error(`Unhandled response type: ${v.responseType}`)
        }
      })
    } catch {
      return initialFilters.traceKeyConditions
    }
  },
}

export const useReportListFilters = () => {
  const history = useHistory()
  const location = useLocation()

  const getFiltersFromQueryString = useCallback((): ReportListFilters => {
    const rawFilters = qs.parse(location.search, qsOptions.parse)
    const traceKeyConditions = sanitize.traceKeyConditions(
      rawFilters['traceKeyConditions'],
    )

    // 日付形式の検索キーがある場合は実施日フィルターを無効化
    const hasDateTraceKeyFilter = traceKeyConditions.some(
      filter =>
        filter.responseType === TraceKeyResponseTypeEnum.Date &&
        filter.datetime?.greaterThanOrEqual &&
        filter.datetime?.lessThan,
    )

    return {
      dateFrom: hasDateTraceKeyFilter
        ? null
        : sanitize.dateFrom(rawFilters['dateFrom']),
      dateTo: hasDateTraceKeyFilter
        ? null
        : sanitize.dateTo(rawFilters['dateTo']),
      deviatedAnswer: sanitize.deviatedAnswer(rawFilters['deviatedAnswer']),
      employeeNameOrCode: sanitize.employeeNameOrCode(
        rawFilters['employeeNameOrCode'],
      ),
      page: sanitize.page(rawFilters['page']),
      pageSize: sanitize.pageSize(rawFilters['pageSize']),
      placeNodeUuids: sanitize.placeNodeUuids(rawFilters['placeNodeUuids']),
      reportName: sanitize.reportName(rawFilters['reportName']),
      statuses: sanitize.statuses(rawFilters['statuses']),
      traceKeyConditions,
    }
  }, [location.search])

  // 1. URL状態（= 検索条件）
  const activeFilters: ReportListFilters = useMemo(
    () => getFiltersFromQueryString(),
    [getFiltersFromQueryString],
  )

  // 2. ローカル状態（UI入力用）
  const [draftFilters, setDraftFilters] = useState<ReportListFilters>(
    () => activeFilters,
  )

  // 検索条件が変更されたらローカル状態と同期
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setDraftFilters(activeFilters)
  }, [activeFilters])

  // 絞り込みを適用するメソッド（ローカル状態をURL/検索条件に反映）
  const executeSearch = useCallback(
    (newFilters: ReportListFilters) => {
      const filtersToSet = removeMatchingKeysAndValues(
        newFilters,
        initialFilters,
      )
      const newQueryString = qs.stringify(filtersToSet, qsOptions.stringify)

      const hasNoUrlChange =
        newQueryString === history.location.search.substring(1)
      if (hasNoUrlChange) return

      history.push({ search: newQueryString })
    },
    [history],
  )
  /**
   * 絞り込みの値を更新する
   *
   * 型のおかげで本来はsanitize関数が不要なプロパティもあるものの、
   * 一貫性を重視してすべてのプロパティに一括してsanitize関数を適用している。
   */
  const setFilterOf = {
    dateRange: (dateFrom: string, dateTo: string) => {
      setDraftFilters(prev => ({
        ...prev,
        dateFrom: sanitize.dateFrom(dateFrom),
        dateTo: sanitize.dateTo(dateTo),
        page: initialFilters.page,
      }))
    },
    deviatedAnswer: (deviatedAnswer: ReportListFilters['deviatedAnswer']) => {
      setDraftFilters(prev => ({
        ...prev,
        deviatedAnswer: sanitize.deviatedAnswer(deviatedAnswer),
        page: initialFilters.page,
      }))
    },
    employeeNameOrCode: (employeeNameOrCode: string) => {
      setDraftFilters(prev => ({
        ...prev,
        employeeNameOrCode: sanitize.employeeNameOrCode(employeeNameOrCode),
        page: initialFilters.page,
      }))
    },
    pageAndPageSize: (page: number, pageSize: number) => {
      const newFilters: ReportListFilters = {
        ...draftFilters,
        page: sanitize.page(page),
        pageSize: sanitize.pageSize(pageSize),
      }
      setDraftFilters(newFilters)
      // ページネーションは即座にURL状態に反映する
      executeSearch(newFilters)
    },
    placeNodeUuids: (placeNodeUuids: string[]) => {
      setDraftFilters(prev => ({
        ...prev,
        placeNodeUuids: sanitize.placeNodeUuids(placeNodeUuids),
        page: initialFilters.page,
      }))
    },
    reportName: (reportName: string) => {
      setDraftFilters(prev => ({
        ...prev,
        reportName: sanitize.reportName(reportName),
        page: initialFilters.page,
      }))
    },
    statuses: (statuses: ReportListFilters['statuses']) => {
      setDraftFilters(prev => ({
        ...prev,
        statuses: sanitize.statuses(statuses),
        page: initialFilters.page,
      }))
    },
    traceKeyConditions: (
      traceKeyConditions: ReportListFilters['traceKeyConditions'],
    ) => {
      setDraftFilters(prev => ({
        ...prev,
        traceKeyConditions: sanitize.traceKeyConditions(traceKeyConditions),
        page: initialFilters.page,
      }))
    },
  }

  const resetFilterOf = {
    // レポート名と期間フィルターはドロワーの外に配置されており、ドロワー内の項目だけをクリアする
    allExceptReportNameAndDateRange: useCallback(() => {
      setDraftFilters(current => ({
        ...initialFilters,
        dateFrom: current.dateFrom,
        dateTo: current.dateTo,
        reportName: current.reportName,
      }))
    }, []),
    dateRange: useCallback(() => {
      const defaultDates = getDefaultDateRange()
      setDraftFilters(prev => ({
        ...prev,
        dateFrom: defaultDates.dateFrom,
        dateTo: defaultDates.dateTo,
      }))
    }, []),
    currentPage: useCallback(() => {
      setDraftFilters(prev => ({
        ...prev,
        page: 1,
      }))
    }, []),
  }

  // 名前がややこしいのは、レポート名・期間フィルターの絞り込み設定UIがドロワーの外に配置されており、他とは独立した挙動を求められていることに起因する
  const isSomeFilterActiveExceptReportNameAndDateRange = (() => {
    const {
      page: _exclude_1,
      pageSize: _exclude_2,
      reportName: _exclude_3,
      dateFrom: _exclude_4,
      dateTo: _exclude_5,
      ...restFilters
    } = activeFilters
    const {
      page: _exclude_6,
      pageSize: _exclude_7,
      reportName: _exclude_8,
      dateFrom: _exclude_9,
      dateTo: _exclude_10,
      ...restInitialFilters
    } = initialFilters
    return !deepEqual(restFilters, restInitialFilters)
  })()

  return {
    // UI表示/入力用の状態
    draftFilters,
    // UI表示/入力用の操作
    setFilterOf,
    resetFilterOf,
    // URL状態（検索に使用）
    activeFilters,
    // URL, 検索条件への反映
    executeSearch,
    // 変更検知
    isSomeFilterActiveExceptReportNameAndDateRange,
  }
}
